import React, { useState, useEffect } from "react"
import { AttendanceTime, attendanceTimeTranslations, attendanceTimeToDate } from "@/types/AttendanceTime"
import { useGlobalSettings } from "@/entities/globalSettings/useGlobalSettings"
import { parseISO, format, startOfDay } from "date-fns"

interface DateTimeSelectorProps {
	onChange: (date: Date | null) => void
	enabledTimeOptions: AttendanceTime[]
	label?: string
	error?: string
	minDateValue?: Date | null
	initialDateValue?: Date | null
	initialTimeOption?: AttendanceTime
}

export const DateTimeSelector: React.FC<DateTimeSelectorProps> = ({
	initialDateValue,
	minDateValue,
	onChange,
	enabledTimeOptions,
	initialTimeOption,
	label,
	error,
}) => {
	const { data: globalSettings } = useGlobalSettings()
	const [date, setDate] = useState<string>(initialDateValue ? format(initialDateValue, "yyyy-MM-dd") : (minDateValue ? format(minDateValue, "yyyy-MM-dd") : ""))
	const [timeType, setTimeType] = useState<AttendanceTime>(initialTimeOption || enabledTimeOptions[0])
	const [specificTime, setSpecificTime] = useState<string>("09:00")

	useEffect(() => {
		if (!minDateValue) return
		if (isNaN(minDateValue.valueOf())) return
		if (startOfDay(minDateValue) > new Date(date)) {
			handleDateChange({ target: { value: format(minDateValue, "yyyy-MM-dd") } } as React.ChangeEvent<HTMLInputElement>)
		}
	}, [minDateValue])

	// Callback principal : output uniquement lors d'un changement utilisateur
	const handleDateChange = (e: React.ChangeEvent<HTMLInputElement>) => {
		const newDate = e.target.value

		if (newDate && minDateValue && !isNaN(minDateValue.valueOf()) && startOfDay(minDateValue) > new Date(newDate)) {
			return
		}

		setDate(newDate)
		if (!newDate || !globalSettings) {
			onChange(null)
			return
		}
		const d = attendanceTimeToDate(
			globalSettings,
			timeType,
			parseISO(newDate),
			timeType === "specific_time" ? specificTime : undefined,
		)
		onChange(d)
	}

	const handleTimeTypeChange = (e: React.ChangeEvent<HTMLSelectElement>) => {
		const newType = e.target.value as AttendanceTime
		setTimeType(newType)
		if (!date || !globalSettings) {
			onChange(null)
			return
		}
		const d = attendanceTimeToDate(
			globalSettings,
			newType,
			parseISO(date),
			newType === "specific_time" ? specificTime : undefined,
		)
		onChange(d)
	}

	const handleSpecificTimeChange = (e: React.ChangeEvent<HTMLInputElement>) => {
		const newTime = e.target.value
		setSpecificTime(newTime)
		if (!date || !globalSettings) {
			onChange(null)
			return
		}
		const d = attendanceTimeToDate(
			globalSettings,
			timeType,
			parseISO(date),
			timeType === "specific_time" ? newTime : undefined,
		)
		onChange(d)
	}

	useEffect(() => {
		// Dès que le globalSettings est chargé, on simule un click pour
		// forcer le calcul de la date au plus tôt
		handleTimeTypeChange({ target: { value: timeType } } as React.ChangeEvent<HTMLSelectElement>)
	}, [globalSettings])


	return (
		<div className="flex flex-col gap-1 w-full">
			{label && <label className="label"><span className="label-text">{label}</span></label>}
			<div className="flex gap-2 w-full">
				<input
					type="date"
					className={`input input-bordered w-1/2 ${error ? "input-error" : ""}`}
					value={date}
					onChange={handleDateChange}
				/>
				<select
					className="select select-bordered w-1/2"
					value={timeType}
					onChange={handleTimeTypeChange}
				>
					{enabledTimeOptions.map((option) => (
						<option key={option} value={option}>
							{attendanceTimeTranslations[option as AttendanceTime]}
						</option>
					))}
				</select>
			</div>
			{timeType === "specific_time" && (
				<div className="flex items-center gap-2 mt-2">
					<div className="w-1/2"></div>
					<input
						type="time"
						className="input input-bordered w-1/2"
						value={specificTime}
						onChange={handleSpecificTimeChange}
						step="900"
					/>
				</div>
			)}
			{error && (
				<label className="label">
					<span className="label-text-alt text-error">{error}</span>
				</label>
			)}
		</div>
	)
}
